import { useState, useCallback, useMemo } from 'react';
import { tokens, Text, Button, Stack, Loader } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { AbstractSingleFilterDropdownProps } from '../SingleFilterType';
import type { ExperimentType } from './ExperimentFilterType';
import { GenericFilterDropdown } from '../GenericBloc/GenericFilter/GenericFilterDropdown';
import { GenericFilterBody } from '../GenericBloc/GenericFilter/GenericFilterBody';
import { GenericFilterFooter } from '../GenericBloc/GenericFilter/GenericFilterFooter';
import { FilterListItem } from '../GenericBloc/GenericSubElement/FilterItemList';
import { ExperimentSelector } from './ExperimentSelector';
import type { Experiment, Variant } from '../../../../domains/experiments/model/Experiment';

const VariantBodyDiv = styled.div`
  padding-left: ${tokens.spacer.x2};
`;

const ErrorDiv = styled.div`
  padding-top: ${tokens.spacer.x1};
`;

const StyledButton = styled(Button)`
  ${tokens.text('body2')}
  min-height: ${tokens.spacer.x4};
`;

const VariantTitle = styled(Text)`
  padding-top: ${tokens.spacer.x1};
`;

enum ValidationErrorEnum {
  SELECT_ONE_EXPERIMENT = 'Select one experiment',
  SELECT_ONE_VARIANT = 'Select at least one variant',
}

function isChecked(option: Variant, dirtyValue: ExperimentType): boolean {
  if (dirtyValue == null) {
    return false;
  }
  return dirtyValue.selectedVariants.some((dirtyOption) => dirtyOption === option.id);
}

function getFilterValueErrors(
  filterValue?: ExperimentType,
  variantOptionList?: Variant[],
): ValidationErrorEnum | undefined {
  if (!filterValue) {
    return ValidationErrorEnum.SELECT_ONE_EXPERIMENT;
  }
  if (
    variantOptionList &&
    variantOptionList.length > 0 &&
    filterValue.selectedVariants.length === 0
  ) {
    return ValidationErrorEnum.SELECT_ONE_VARIANT;
  }
  return undefined;
}

function getVariantOptions(optionList: Experiment[], selectedExperimentId?: string): Variant[] {
  return optionList.find((item) => item.experimentId === selectedExperimentId)?.variants || [];
}

const DROPDOWN_WIDTH = 300;

interface ExperimentDropdownProps extends AbstractSingleFilterDropdownProps<ExperimentType> {
  optionList: Experiment[];
  optionListIsLoading: boolean;
  isIntegrationActive: boolean;
}

export function ExperimentDropdown({
  currentValue,
  onApply,
  filterName,
  optionList = [],
  optionListIsLoading,
  isIntegrationActive,
}: ExperimentDropdownProps): JSX.Element {
  const [dirtyValue, setDirtyValue] = useState(currentValue);
  const [variantOptionList, setVariantOptionList] = useState(
    getVariantOptions(optionList, dirtyValue?.experimentId),
  );
  const [validationError, setValidationError] = useState<ValidationErrorEnum>();

  const handleExperimentSelected = useCallback(
    (newValue) => {
      setDirtyValue({
        experimentId: newValue.experimentId,
        containerId: newValue.containerId,
        selectedVariants: [],
      });
      setVariantOptionList(getVariantOptions(optionList, newValue.experimentId));
      setValidationError(undefined);
    },
    [optionList],
  );

  const handleVariantSelected = useCallback(
    (newValue) => {
      if (dirtyValue) {
        const oldSelectedVariants = dirtyValue.selectedVariants;
        const newSelectedVariants = oldSelectedVariants.includes(newValue)
          ? oldSelectedVariants.filter((dirtyItem) => dirtyItem !== newValue)
          : [...oldSelectedVariants, newValue];

        setDirtyValue({
          experimentId: dirtyValue.experimentId,
          containerId: dirtyValue.containerId,
          selectedVariants: newSelectedVariants,
        });
        setValidationError(undefined);
      }
    },
    [dirtyValue],
  );

  useMemo(() => {
    if (!optionListIsLoading && dirtyValue?.experimentId != null) {
      setVariantOptionList(getVariantOptions(optionList, dirtyValue?.experimentId));
    }
  }, [optionListIsLoading, dirtyValue?.experimentId, optionList]);

  const handleApply = useCallback(() => {
    const filterValueErrors = getFilterValueErrors(dirtyValue, variantOptionList);
    if (filterValueErrors) {
      setValidationError(filterValueErrors);
    } else {
      onApply(dirtyValue);
    }
  }, [dirtyValue, onApply, variantOptionList]);

  if (!dirtyValue) {
    return (
      <ExperimentSelector
        filterName={filterName}
        optionList={optionList}
        optionListIsLoading={optionListIsLoading}
        onExperimentSelected={handleExperimentSelected}
        customWidth={DROPDOWN_WIDTH}
        isIntegrationActive={isIntegrationActive}
      />
    );
  }

  return (
    <GenericFilterDropdown
      ariaLabel={`${filterName} dropdown`}
      customWidth={DROPDOWN_WIDTH}
      onApply={handleApply}
    >
      <GenericFilterBody>
        <Stack direction="column" spacing="x0">
          <Text textType="subtitle2">{filterName === 'Unbounce' ? 'Page' : 'Experiment'}</Text>
          <Text textType="body2">
            <StyledButton kind="ghost" size="s" onClick={() => setDirtyValue(undefined)}>
              {`# ${dirtyValue.experimentId}`}
            </StyledButton>
          </Text>

          <VariantTitle textType="subtitle2">Variants</VariantTitle>
          {optionListIsLoading && <Loader size="l" isGreyscale />}
          {!optionListIsLoading && (
            <VariantBodyDiv>
              {(variantOptionList == null || variantOptionList.length === 0) && (
                <Text textType="body2">None for this experiment</Text>
              )}
              <Stack direction="column" spacing="x0">
                {variantOptionList != null &&
                  variantOptionList.map((option) => {
                    return (
                      <FilterListItem
                        isChecked={isChecked(option, dirtyValue)}
                        id={`${filterName}-dropdown-${option.id}`}
                        key={option.id}
                        name={option.id}
                        onChange={() => handleVariantSelected(option.id)}
                        value={`${option.id}`}
                        labelContent={option.id}
                        isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_VARIANT}
                      />
                    );
                  })}
              </Stack>
            </VariantBodyDiv>
          )}
        </Stack>
        {!!validationError && (
          <ErrorDiv>
            <Text textColor="text_error">{validationError}</Text>
          </ErrorDiv>
        )}
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
